/**
 * Topbar data-confidence chip. Reads the active analysis's confidence from
 * ShellStatusContext (published by ProjectLayout) and shows it as a glass-box
 * indicator: green validated / amber estimated / red unreliable, with a popover
 * summarising the provenance. Renders nothing when no analysis is active.
 */
import React, { useState, useRef, useEffect } from 'react';
import { ShieldCheck, WarningCircle } from '@phosphor-icons/react';
import { useShellStatus } from '../../context/ShellStatusContext';

const LEVELS = {
  high:     { token: 'var(--aipron-success)', es: 'Validado',  en: 'Validated',  Icon: ShieldCheck },
  degraded: { token: 'var(--aipron-warning)', es: 'Estimado',  en: 'Estimated',  Icon: WarningCircle },
  low:      { token: 'var(--aipron-danger)',  es: 'No fiable', en: 'Unreliable', Icon: WarningCircle },
} as const;

const ConfidenceChip: React.FC<{ lang: 'es' | 'en' }> = ({ lang }) => {
  const { status } = useShellStatus();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (!status) return null;
  const lvl = LEVELS[status.confidence];
  const es = lang === 'es';
  const label = es ? lvl.es : lvl.en;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={es ? 'Confianza del análisis' : 'Analysis confidence'}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 10px',
          borderRadius: 20, cursor: 'pointer', fontSize: 11.5, fontWeight: 600, letterSpacing: '.02em',
          color: lvl.token, background: `color-mix(in srgb, ${lvl.token} 12%, transparent)`,
          border: `1px solid color-mix(in srgb, ${lvl.token} 35%, transparent)`,
        }}
      >
        <lvl.Icon size={14} weight="fill" />
        <span>{label}</span>
      </button>

      {open && (
        <div
          role="dialog"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 268, zIndex: 500,
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
            padding: '14px 16px', boxShadow: '0 8px 24px rgba(0,0,0,.14)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
            <lvl.Icon size={16} weight="fill" style={{ color: lvl.token }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
              {es ? 'Confianza de datos' : 'Data confidence'}: <span style={{ color: lvl.token, textTransform: 'uppercase' }}>{status.confidence}</span>
            </span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 10px' }}>
            {status.confidence === 'high'
              ? (es ? 'Todos los KPI se calcularon sobre datos reales del proyecto.' : 'Every KPI was computed from the project\'s real data.')
              : (es ? 'Uno o más KPI se construyeron sobre valores por defecto, no sobre datos reales.' : 'One or more KPIs were built on default values, not on real data.')}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 10px', fontSize: 11.5, fontFamily: 'var(--font-mono, monospace)' }}>
            <span style={{ color: 'var(--text-tertiary)' }}>{es ? 'Metodología' : 'Methodology'}</span>
            <span style={{ color: 'var(--text-primary)' }}>v{status.methodologyVersion ?? '—'}</span>
            <span style={{ color: 'var(--text-tertiary)' }}>{es ? 'KPI degradados' : 'Degraded KPIs'}</span>
            <span style={{ color: status.flagsCount ? lvl.token : 'var(--text-primary)' }}>{status.flagsCount}</span>
            <span style={{ color: 'var(--text-tertiary)' }}>{es ? 'Valores por defecto' : 'Defaults used'}</span>
            <span style={{ color: 'var(--text-primary)' }}>{status.defaultsCount}</span>
          </div>
          {status.confidence !== 'high' && (
            <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: '10px 0 0', lineHeight: 1.4 }}>
              {es ? 'Detalle completo en el panel del análisis.' : 'Full detail in the analysis panel.'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfidenceChip;
